import { useState } from 'react'
import { AlertCircle, Plus } from 'lucide-react'
import { useTranslation } from '@kostbar/i18n'
import { useCategories, useUnassignedProducts } from '../hooks/use-categories'
import { CategoryTable } from '../components/category-table'
import { CategoryForm } from '../components/category-form'
import { UnassignedTable } from '../components/unassigned-table'
import type { Category } from '@/api/types'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { PermissionGate } from '@/shared/components/permission-gate'
import { usePermissions } from '@/shared/hooks/use-permissions'
import { ACTIONS } from '@/shared/constants/actions'

export function CategoriesPage() {
  const { t: tCategories } = useTranslation('categories')
  const [formOpen, setFormOpen] = useState(false)
  const [editing, setEditing] = useState<Category | null>(null)
  const [presetParent, setPresetParent] = useState<Category | null>(null)
  const { can } = usePermissions()
  const canCreate = can(ACTIONS.CATEGORY_CREATE)
  const { data, isLoading, error } = useCategories()

  // Only the counts are needed here; the tab badge has to be visible without opening the tab.
  const { data: worklist } = useUnassignedProducts({ pageSize: 1 })

  const categories = data?.items || []
  const openCount = worklist?.total ?? 0

  const openCreate = () => {
    setEditing(null)
    setPresetParent(null)
    setFormOpen(true)
  }

  const openEdit = (category: Category) => {
    setEditing(category)
    setPresetParent(null)
    setFormOpen(true)
  }

  const openAddChild = (parent: Category) => {
    setEditing(null)
    setPresetParent(parent)
    setFormOpen(true)
  }

  return (
    <PermissionGate action={ACTIONS.CATEGORY_VIEW}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">{tCategories('title')}</h1>
            <p className="text-muted-foreground mt-1">
              {tCategories('subtitle')}
            </p>
          </div>
          {canCreate && (
            <Button onClick={openCreate}>
              <Plus className="mr-2 h-4 w-4" />
              {tCategories('createNew')}
            </Button>
          )}
        </div>

        {/* Error Alert */}
        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>{tCategories('error.title')}</AlertTitle>
            <AlertDescription>
              {tCategories('error.description')}
            </AlertDescription>
          </Alert>
        )}

        <Tabs defaultValue="structure">
          <TabsList>
            <TabsTrigger value="structure">
              {tCategories('tabs.structure')}
            </TabsTrigger>
            <TabsTrigger value="unassigned" className="gap-2">
              {tCategories('tabs.unassigned')}
              {openCount > 0 && (
                <Badge variant="secondary" className="tabular-nums">
                  {openCount}
                </Badge>
              )}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="structure" className="mt-4">
            <CategoryTable
              categories={categories}
              isLoading={isLoading}
              onEdit={canCreate ? openEdit : undefined}
              onAddChild={canCreate ? openAddChild : undefined}
            />
          </TabsContent>

          <TabsContent value="unassigned" className="mt-4">
            <UnassignedTable categories={categories} />
          </TabsContent>
        </Tabs>

        {/* Form Dialog */}
        <CategoryForm
          open={formOpen}
          onOpenChange={setFormOpen}
          categories={categories}
          editing={editing}
          presetParent={presetParent}
        />
      </div>
    </PermissionGate>
  )
}
